import React from "react";
import { Link } from "react-router-dom";
import { Hourglass, ArrowRight, Sparkles } from "lucide-react";

// Semicircular "aging dial": age 20 (left) → 90 (right). A marker sits at biological age,
// a faint tick marks chronological age, and the gap is called out in the centre.
const CX = 150;
const CY = 140;
const R = 112;
const MIN_AGE = 20;
const MAX_AGE = 90;

const pt = (angleDeg, radius) => {
  const a = (Math.PI / 180) * angleDeg;
  return [CX + radius * Math.cos(a), CY - radius * Math.sin(a)];
};
const angleForAge = (age) => {
  const clamped = Math.max(MIN_AGE, Math.min(age, MAX_AGE));
  const t = (clamped - MIN_AGE) / (MAX_AGE - MIN_AGE);
  return 180 * (1 - t);
};
const arcPath = (startAngle, endAngle, r) => {
  const [x1, y1] = pt(startAngle, r);
  const [x2, y2] = pt(endAngle, r);
  const largeArc = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
  const sweep = startAngle > endAngle ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} ${sweep} ${x2} ${y2}`;
};

const gapColor = (gap) => (gap <= -0.5 ? "#3F6B4C" : gap >= 2 ? "#C05C42" : "#B07D2B");
const gapWord = (gap) => {
  if (gap <= -0.5) return `${Math.abs(gap)} yr${Math.abs(gap) === 1 ? "" : "s"} younger`;
  if (gap >= 0.5) return `${gap} yr${gap === 1 ? "" : "s"} older`;
  return "on par";
};

function Dial({ bio, chrono, gap, provisional, symptomScore }) {
  const showAge = bio != null;
  const bioAngle = showAge ? angleForAge(bio) : 90;
  const chronoAngle = chrono != null ? angleForAge(chrono) : null;
  const color = showAge ? gapColor(gap) : "#C05C42";
  const [bx, by] = pt(bioAngle, R);
  const centerVal = showAge ? bio : (provisional && symptomScore != null ? symptomScore : "—");

  return (
    <svg viewBox="0 0 300 175" className="w-[260px] max-w-full" role="img" aria-label="Biological age dial">
      <defs>
        <linearGradient id="bioTrack" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#3F6B4C" />
          <stop offset="50%" stopColor="#D49A44" />
          <stop offset="100%" stopColor="#C05C42" />
        </linearGradient>
      </defs>
      {/* base track */}
      <path d={arcPath(180, 0, R)} fill="none" stroke="#E2D8C8" strokeWidth="16" strokeLinecap="round" />
      {/* coloured progress up to biological age */}
      {showAge && (
        <path d={arcPath(180, bioAngle, R)} fill="none" stroke={color} strokeWidth="16" strokeLinecap="round" strokeDasharray={provisional ? "3 6" : undefined} />
      )}
      {/* chronological tick */}
      {chronoAngle != null && (() => {
        const [ix, iy] = pt(chronoAngle, R - 12);
        const [ox, oy] = pt(chronoAngle, R + 12);
        return <line x1={ix} y1={iy} x2={ox} y2={oy} stroke="#2D322B" strokeWidth="2.5" strokeLinecap="round" />;
      })()}
      {/* biological-age marker */}
      {showAge && <circle cx={bx} cy={by} r="9" fill={color} stroke="#F9F6F0" strokeWidth="3" />}
      {/* scale labels */}
      <text x="26" y="168" fontSize="11" fill="#9A8F7E">{MIN_AGE}</text>
      <text x="262" y="168" fontSize="11" fill="#9A8F7E">{MAX_AGE}</text>
      {/* centre value */}
      <text x={CX} y={CY - 20} textAnchor="middle" fontSize="52" fontFamily="Georgia, serif" fill="#2D322B">{centerVal}</text>
      <text x={CX} y={CY + 2} textAnchor="middle" fontSize="11" letterSpacing="1.5" fill="#5C6359">
        {showAge ? (provisional ? "EST. BIO AGE" : "BIOLOGICAL AGE") : "BIO-AGE"}
      </text>
    </svg>
  );
}

export default function BioAgeCard({ bioage }) {
  if (!bioage) return null;
  const { chronological_age: chrono, biological_age: bio, age_gap: gap, provisional, symptom_score, estimate_source } = bioage;
  const needsIntake = !chrono;
  const estimated = !provisional && bio != null;
  const lifestyle = provisional && estimate_source === "lifestyle" && bio != null;

  return (
    <Link to="/bioage" className="mt-8 block group" data-testid="bioage-hero-card">
      <div className="bg-[#2D322B] rounded-3xl p-8 sm:p-10 flex flex-col md:flex-row md:items-center gap-8 group-hover:ring-2 group-hover:ring-[#D49A44]/50 transition-all overflow-hidden relative">
        <div className="shrink-0 flex justify-center md:block">
          {needsIntake ? (
            <div className="w-[260px] h-[150px] flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#F9F6F0]/25">
              <Hourglass className="w-8 h-8 text-[#D49A44] mb-2" />
              <p className="text-[#F9F6F0]/70 text-sm">Complete intake to unlock</p>
            </div>
          ) : (
            <Dial bio={bio} chrono={chrono} gap={gap} provisional={provisional} symptomScore={symptom_score} />
          )}
        </div>

        <div className="flex-1 text-[#F9F6F0]">
          <p className="label-eyebrow !text-[#D49A44] mb-2">Your vitality mirror</p>
          {needsIntake ? (
            <>
              <p className="font-serif text-3xl leading-tight mb-2">Discover your biological age.</p>
              <p className="text-[#F9F6F0]/70 text-sm max-w-md">Complete your quick intake and add a lab panel to see how your body is really ageing — and how to move the number.</p>
            </>
          ) : estimated ? (
            <>
              <p className="font-serif text-3xl leading-tight mb-1">
                Your body reads <span style={{ color: gapColor(gap) === "#3F6B4C" ? "#8FBF9F" : gapColor(gap) === "#C05C42" ? "#E9967A" : "#E4B564" }}>{gapWord(gap)}</span>
              </p>
              <p className="text-[#F9F6F0]/70 text-sm max-w-md mb-3">
                Biological age <strong className="text-[#F9F6F0]">{bio}</strong> vs chronological <strong className="text-[#F9F6F0]">{chrono}</strong>.
                A modifiable snapshot — your protocols are built to shift it.
              </p>
              {bioage.missing_markers?.length > 0 && (
                <p className="text-[10px] uppercase tracking-widest text-[#F9F6F0]/45" data-testid="bioage-hero-confidence">
                  Based on {bioage.markers_used?.length ?? 0} of {(bioage.markers_used?.length ?? 0) + bioage.missing_markers.length} markers
                </p>
              )}
            </>
          ) : lifestyle ? (
            <>
              <p className="font-serif text-3xl leading-tight mb-1">
                Provisional estimate: <span style={{ color: gapColor(gap) === "#3F6B4C" ? "#8FBF9F" : gapColor(gap) === "#C05C42" ? "#E9967A" : "#E4B564" }}>{bio}</span>
              </p>
              <p className="text-[#F9F6F0]/70 text-sm max-w-md mb-2">
                A lifestyle-based estimate ({gapWord(gap)} than your chronological {chrono}) from your intake & check-in.
              </p>
              <p className="text-[10px] uppercase tracking-widest text-[#E4B564]" data-testid="bioage-hero-lifestyle">
                Estimate — refine with a lab panel
              </p>
            </>
          ) : (
            <>
              <p className="font-serif text-3xl leading-tight mb-2">Add your labs to reveal your biological age.</p>
              <p className="text-[#F9F6F0]/70 text-sm max-w-md">
                {symptom_score != null
                  ? `Your provisional wellness score is ${symptom_score}/100. Upload a lab panel to unlock your full PhenoAge estimate.`
                  : "Take your weekly check-in and upload a lab panel to begin."}
              </p>
            </>
          )}
          {(estimated || lifestyle) && bioage.improvement_potential && (
            <p className="mt-3 text-xs text-[#E4B564] flex items-center gap-1.5" data-testid="bioage-hero-nudge">
              <Sparkles className="w-3.5 h-3.5" />
              {bioage.top_protocol ? `Your ${bioage.top_protocol} could shed ~${bioage.improvement_potential} yr${bioage.improvement_potential === 1 ? "" : "s"}.` : `Your protocols could shed ~${bioage.improvement_potential} yr${bioage.improvement_potential === 1 ? "" : "s"}.`}
            </p>
          )}
          <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#D49A44] group-hover:text-[#F9F6F0] transition-colors" data-testid="bioage-hero-cta">
            {needsIntake ? "Get started" : estimated ? "Explore your Bio-Age" : lifestyle ? "Refine with labs" : "Add labs"} <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
